import type { HTMLAttributes, LabelHTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/utils.js';

export type FieldWidth = 'quantity' | 'percent' | 'money' | 'date' | 'code' | 'short-text' | 'fill';

const FIELD_WIDTH_CLASS: Record<FieldWidth, string> = {
  quantity: 'zr-field-w--quantity',
  percent: 'zr-field-w--percent',
  money: 'zr-field-w--money',
  date: 'zr-field-w--date',
  code: 'zr-field-w--code',
  'short-text': 'zr-field-w--short-text',
  fill: 'zr-field-w--fill',
};

export function fieldWidthClassName(width: FieldWidth): string {
  return FIELD_WIDTH_CLASS[width];
}

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  width?: FieldWidth;
}

export function Field({ children, className, width, ...props }: FieldProps) {
  return (
    <div
      className={cn('field', width !== undefined && fieldWidthClassName(width), className)}
      {...props}
    >
      {children}
    </div>
  );
}

export interface FieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  children: ReactNode;
  required?: boolean;
}

export function FieldLabel({ children, className, required = false, ...props }: FieldLabelProps) {
  return (
    <label className={cn('field-label', className)} {...props}>
      {children}
      {required ? <span className="req">*</span> : null}
    </label>
  );
}

export interface FieldHelpProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode;
}

export function FieldHelp({ children, className, ...props }: FieldHelpProps) {
  return (
    <p className={cn('field-help', className)} {...props}>
      {children}
    </p>
  );
}

export interface FieldErrorProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode;
}

export function FieldError({ children, className, ...props }: FieldErrorProps) {
  return (
    <p className={cn('field-error', className)} role="alert" {...props}>
      {children}
    </p>
  );
}
